import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

interface User {
  email: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Validate and refresh token
  const validateAndRefreshToken = async (fbUser: FirebaseUser) => {
    try {
      // Force token refresh to validate it's still active
      const token = await fbUser.getIdToken(true);
      setUser({ email: fbUser.email!, token });
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      // Token is invalid, logout user
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
      // Clear any cached data
      sessionStorage.clear();
      localStorage.removeItem('language'); // Keep language preference
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser && fbUser.email) {
        await validateAndRefreshToken(fbUser);
      } else {
        setUser(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Periodic token validation - check every 5 minutes
  useEffect(() => {
    if (firebaseUser) {
      // Clear any existing interval
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }

      // Set up periodic validation
      tokenRefreshIntervalRef.current = setInterval(async () => {
        if (firebaseUser) {
          const isValid = await validateAndRefreshToken(firebaseUser);
          if (!isValid) {
            console.log('Session expired - logging out');
          }
        }
      }, 5 * 60 * 1000); // 5 minutes
    } else {
      // Clear interval when logged out
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
    }

    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, [firebaseUser]);

  // Clear cache on page visibility change (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && firebaseUser) {
        // Revalidate token when user returns to the page
        await validateAndRefreshToken(firebaseUser);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [firebaseUser]);

  const logout = async () => {
    // Clear token refresh interval
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }

    // Clear all cached data except language preference
    const lang = localStorage.getItem('language');
    sessionStorage.clear();
    localStorage.clear();
    if (lang) {
      localStorage.setItem('language', lang);
    }

    // Sign out from Firebase
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        isAuthenticated: !!firebaseUser,
        isLoading,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
